import type { DayLabel } from '../types';
import { easterSunday } from './easter';
import { allSeasons } from './seasons';

const SEASON_LABEL: Record<string, string> = {
  PRINTEMPS: 'PRINTEMPS',
  ETE: 'ÉTÉ',
  AUTOMNE: 'AUTOMNE',
  HIVER: 'HIVER',
};

export function isoKey(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

// Last Sunday of May, shifted to first Sunday of June if it coincides with Pentecôte.
function mothersDay(year: number, pentecote: Date): Date {
  const may31 = new Date(year, 4, 31);
  const offset = may31.getDay(); // 0 = Sunday
  let d = new Date(year, 4, 31 - offset);
  if (d.getTime() === pentecote.getTime()) {
    d = addDays(d, 7);
  }
  return d;
}

// 3rd Sunday of June.
function fathersDay(year: number): Date {
  const june1 = new Date(year, 5, 1);
  const offsetToSunday = (7 - june1.getDay()) % 7;
  return new Date(year, 5, 1 + offsetToSunday + 14);
}

// Sunday before the 1st Sunday of Advent (i.e. last Sunday on or before Nov 26).
// Original used Nov 20 as anchor; that yields Sunday on or after Nov 20.
function christKingDay(year: number): Date {
  const anchor = new Date(year, 10, 20);
  const day = anchor.getDay();
  if (day === 0) return anchor;
  return new Date(year, 10, 27 - day);
}

// Annonciation: 25 March, shifted if it falls in Holy Week or on a Sunday.
function annonciation(year: number, easter: Date): Date {
  const base = new Date(year, 2, 25);
  // If 25 March is within the week before Easter (Palm Sunday through Easter Saturday),
  // it is moved to the Monday after Quasimodo (8 days after Easter).
  const diff = Math.round((easter.getTime() - base.getTime()) / 86400000);
  if (diff >= 0 && diff < 8) {
    return addDays(easter, 8);
  }
  if (base.getDay() === 0) {
    return addDays(base, 1);
  }
  return base;
}

export function buildLabels(year: number): Map<string, DayLabel> {
  const out = new Map<string, DayLabel>();
  const setHoliday = (m: number, d: number, text: string) => {
    out.set(isoKey(year, m, d), { text, kind: 'holiday' });
  };
  const setSpecial = (date: Date, text: string) => {
    const key = isoKey(date.getFullYear(), date.getMonth(), date.getDate());
    if (!out.has(key)) out.set(key, { text, kind: 'special' });
  };

  // Fixed holidays
  setHoliday(0, 1,  "JOUR de l'AN");
  setHoliday(4, 1,  'F. du TRAVAIL');
  setHoliday(4, 8,  'VICTOIRE 45');
  setHoliday(6, 14, 'FÊTE NAT.');
  setHoliday(7, 15, 'ASSOMPTION');
  setHoliday(10, 1, 'TOUSSAINT');
  setHoliday(10, 11, 'ARMISTICE 18');
  setHoliday(11, 25, 'NOËL');

  // Easter-derived
  const easter = easterSunday(year);
  setSpecial(addDays(easter, -47), 'Mardi-Gras');
  setSpecial(addDays(easter, -46), 'Cendres');
  setSpecial(addDays(easter, -7), 'Rameaux');
  setSpecial(addDays(easter, -2), 'Vendredi Saint');
  setSpecial(easter, 'PÂQUES');
  // Holidays around Easter — override special if needed.
  out.set(isoKey(year, addDays(easter, 1).getMonth(), addDays(easter, 1).getDate()), {
    text: 'L. de PÂQUES', kind: 'holiday',
  });
  const ascension = addDays(easter, 39);
  out.set(isoKey(year, ascension.getMonth(), ascension.getDate()), { text: 'ASCENSION', kind: 'holiday' });
  const pentecote = addDays(easter, 49);
  setSpecial(pentecote, 'PENTECÔTE');
  const lundiPent = addDays(easter, 50);
  out.set(isoKey(year, lundiPent.getMonth(), lundiPent.getDate()), { text: 'L. de PENT.', kind: 'holiday' });

  // Family days
  setSpecial(fathersDay(year), 'F. des Pères');
  setSpecial(mothersDay(year, pentecote), 'F. des Mères');

  // Annonciation
  setSpecial(annonciation(year, easter), 'Annonciation');

  // Christ Roi & Avent
  const christRoi = christKingDay(year);
  setSpecial(christRoi, 'Christ Roi');
  setSpecial(addDays(christRoi, 7), 'Avent');

  // Épiphanie (6 January — original used quantieme 5 which is Jan 6).
  setSpecial(new Date(year, 0, 6), 'Épiphanie');

  // Equinoxes & solstices
  for (const { key, date } of allSeasons(year)) {
    setSpecial(date, SEASON_LABEL[key] ?? key);
  }

  return out;
}
